// src/components/private/highgarden/ProductOffers.js
// Component to show all product offers to the logged-in user

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Card, Alert, Pagination, OverlayTrigger, Tooltip } from 'react-bootstrap';
import FloatingActionButton from '../../common_components/FloatingActionButton';
import { getCurrencySymbol } from '../../common_components/choices';
import './offers.css';
import ReactCardFlip from 'react-card-flip';
import Select from 'react-select';
import AddOffer from './UserOfferAdd';
import ProductOfferDetailsModal from './ProductOfferDetailsModal';
import { getPaginationItems } from '../../common_components/getPaginationItems';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { getCategoryIcon } from '../services';

const ProductOffers = () => {
    const [productOffers, setProductOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [flippedCards, setFlippedCards] = useState({});
    const [categories, setCategories] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);

    // Pagination states
    const pageSize = 9;
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // State for grocery lists
    const [defaultGroceryList, setDefaultGroceryList] = useState(null);

    // State hooks for filters
    const [filters, setFilters] = useState({
        searchTerm: '',
        favProducts: true, // Show only favorite products by default
        category_slug: null,
    });

    // Retrieve the token from local storage
    const token = localStorage.getItem('token');

    // Fetch product categories
    const fetchCategories = useCallback(async () => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-categories/`, {
                headers: { Authorization: `Token ${token}` },
            });
            setCategories(data.results);
        } catch (error) {
            console.error('Failed to fetch product categories:', error);
        }
    }, [token]);

    // Store debounced function in useRef to maintain across renders
    const debouncedFetchRef = useRef(
        debounce(async (filters = {}, page = 1) => {
            if (filters.searchTerm && filters.searchTerm.length < 3) return;

            try {
                setLoading(true);
                const queryFilters = {
                    search: filters.searchTerm || '',
                    fav_products: filters.favProducts ? 'true' : '',
                    category_slug: filters.category_slug !== null ? filters.category_slug.value : '',
                    offer_status: 'ACTIVE',
                    fav_stores: 'true', // Product offers for favorite stores
                    page_size: pageSize,
                    page: page,
                };
                const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-product-offers/`, {
                    params: queryFilters,
                    headers: { Authorization: `Token ${token}` },
                });
                setProductOffers(data.results);
                setTotalPages(Math.ceil(data.count / pageSize));
                setError('');
            } catch (error) {
                setError('Failed to fetch product offers');
                console.error('Failed to fetch product offers:', error);
            } finally {
                setLoading(false);
            }
        }, 300) // 300ms delay for debouncing
    );

    const fetchProductOffers = useCallback((filters = {}, page = 1) => {
        debouncedFetchRef.current(filters, page);
    }, []);

    const fetchUserGroceryLists = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/household-grocery-lists/`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            if (response.data.results.length > 0) {
                const defaultList = response.data.results.find(list => list.is_default === true);
                setDefaultGroceryList(defaultList || response.data.results[0]);
            } else {
                setDefaultGroceryList(null);
                console.warn("No grocery lists found.");
            }
        } catch (error) {
            console.error("Error fetching user grocery lists:", error);
        }
    };

    const addProductToGroceryList = async (product) => {
        if (!defaultGroceryList) {
            console.error("No default grocery list found.");
            return;
        }
        // Find the offer with the lowest price
        const lowestOffer = product.offers.reduce((minOffer, currentOffer) => {
            const minPrice = minOffer.offer_price_details.length ? minOffer.offer_price_details[0].price : Infinity;
            const currentPrice = currentOffer.offer_price_details.length ? currentOffer.offer_price_details[0].price : Infinity;
            return currentPrice < minPrice ? currentOffer : minOffer;
        }, product.offers[0]);

        if (!lowestOffer || !lowestOffer.offer_price_details.length) {
            console.error("No valid offers found for this product.");
            toast.error("Unable to add product due to missing price information.");
            return;
        }

        const purchasePrice = lowestOffer.offer_price_details[0].price;
        const purchasePriceCurrency = lowestOffer.offer_price_details[0].currency;
        const offerId = lowestOffer.id
        const storeId = lowestOffer.stores[0].id

        const payload = {
            list_uuid: defaultGroceryList.uuid,
            name: product.name,
            quantity: 1,
            product: product.id,
            store: storeId,
            purchase_price: purchasePrice,
            purchase_price_currency: purchasePriceCurrency,
            offer: offerId,
        };
        console.log("Adding product to grocery list:", JSON.stringify(payload, null, 2));
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`, payload, {
                headers: { 'Authorization': `Token ${token}` }
            });
            toast.success(`Product added to ${defaultGroceryList.name}!`);
        } catch (error) {
            toast.error("Error adding product to grocery list:", error);
            console.error("Error adding product to grocery list:", error);
        }
    };

    useEffect(() => {
        fetchProductOffers(filters, currentPage);
        fetchCategories();
        fetchUserGroceryLists();
    }, [fetchProductOffers, fetchCategories, filters, currentPage]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, [name]: value };
            fetchProductOffers(newFilters, 1);
            setCurrentPage(1); // Reset to first page when filters change
            return newFilters;
        });
    };

    const handleFavProductsToggle = () => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, favProducts: !prevFilters.favProducts };
            fetchProductOffers(newFilters, 1);
            setCurrentPage(1); // Reset to first page when filters change
            return newFilters;
        });
    };

    const handleCategoryChange = (selectedOption) => {
        setFilters((prevFilters) => {
            const newFilters = { ...prevFilters, category_slug: selectedOption };
            fetchProductOffers(newFilters, 1);
            setCurrentPage(1); // Reset to first page when filters change
            return newFilters;
        });
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleFlip = (productId) => {
        setFlippedCards((prev) => ({
            ...prev,
            [productId]: !prev[productId],
        }));
    };

    const handleShowDetails = (offer) => {
        setSelectedOffer(offer);
        setShowDetailsModal(true);
    };

    return (
        <Container padding="0">
            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search"
                            name="searchTerm"
                            value={filters.searchTerm}
                            onChange={handleFilterChange}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Select
                            isClearable
                            placeholder="Filter by category"
                            value={filters.category_slug}
                            options={categories.map((category) => ({
                                value: category.slug,
                                label: category.name
                            }))}
                            onChange={handleCategoryChange}
                        />
                    </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-center">
                    <Form.Group className="mb-3">
                        <Form.Check
                            type="switch"
                            id="favProducts-switch"
                            label="Show favorite products"
                            checked={filters.favProducts}
                            onChange={handleFavProductsToggle}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col>
                    {loading ? (
                        <p>Loading product offers...</p>
                    ) : error ? (
                        <Alert variant="danger">{error}</Alert>
                    ) : productOffers.length > 0 ? (
                        <Row>
                            {productOffers.map((product) => {
                                const lowestOffer = product.offers.reduce((min, offer) => {
                                    const minPrice = min.offer_price_details.length ? min.offer_price_details[0].price : Infinity;
                                    const offerPrice = offer.offer_price_details.length ? offer.offer_price_details[0].price : Infinity;
                                    return offerPrice < minPrice ? offer : min;
                                }, product.offers[0]);

                                return (
                                    <Col key={product.id} md={4} style={{ marginBottom: '1rem' }}>
                                        <ReactCardFlip isFlipped={flippedCards[product.id]} flipDirection="horizontal">
                                            <Card className="offer-card offer-card-front" onClick={() => handleFlip(product.id)} style={{ cursor: "pointer", position: 'relative' }}>
                                                <Card.Img
                                                    variant="top"
                                                    as="div"
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: '70px',
                                                        width: '70px',
                                                        margin: 'auto'
                                                    }}
                                                >
                                                    <i className={`bi ${getCategoryIcon(product.category.slug)}`} style={{ fontSize: '2rem' }}></i>
                                                </Card.Img>


                                                <Card.Body className="offer-card-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Card.Title className="card-title" style={{ width: 'calc(100% - 30px)', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                        {product.name}
                                                    </Card.Title>
                                                    <Card.Text className="offer-price">
                                                        Offer Price: {lowestOffer.offer_price_details.length ? `${lowestOffer.offer_price_details[0].price} ${getCurrencySymbol(lowestOffer.offer_price_details[0].currency)}` : 'N/A'}
                                                    </Card.Text>
                                                    <Card.Text className="card-text">
                                                        {product.offers.length} {product.offers.length === 1 ? 'offer' : 'offers'}
                                                    </Card.Text>
                                                    <i
                                                        className="bi bi-info-circle"
                                                        style={{ position: 'absolute', top: '5px', right: '5px', fontSize: '1.2rem', color: '#007bff', cursor: 'pointer' }}
                                                        onClick={(e) => { e.stopPropagation(); handleShowDetails(product); }}
                                                    ></i>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id={`tooltip-${product.id}`}>Add to default grocery list</Tooltip>}
                                                    >
                                                        <i
                                                            className="bi bi-plus-circle"
                                                            style={{ position: 'absolute', bottom: '5px', right: '5px', fontSize: '1.2rem', color: '#007bff', cursor: 'pointer' }}
                                                            onClick={(e) => { e.stopPropagation(); addProductToGroceryList(product); }}
                                                        ></i>
                                                    </OverlayTrigger>
                                                </Card.Body>
                                            </Card>
                                            <Card className="offer-card offer-card-back" onClick={() => handleFlip(product.id)} style={{ cursor: "pointer" }}>
                                                <Card.Body>
                                                    <Card.Title className="card-title">{product.name}</Card.Title>
                                                    <Card.Text className="card-text">{product.description || 'No description available.'}</Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </ReactCardFlip>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <Alert variant="info">No product offers available at the moment.</Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="fixed-pagination">
                        <Pagination>
                            <Pagination.Prev
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            />
                            {getPaginationItems(currentPage, totalPages, 11, handlePageChange)}
                            <Pagination.Next
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            />
                        </Pagination>
                    </div>
                </Col>
            </Row>
            <FloatingActionButton
                actions={[
                    {
                        icon: "bi bi-plus",
                        tooltipText: "Add an Offer",
                        handler: () => setShowAddModal(true),
                        tooltip: "Add an Offer",
                    }
                ]}
            />
            <AddOffer
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
            />
            <ProductOfferDetailsModal
                showModal={showDetailsModal}
                setShowModal={setShowDetailsModal}
                selectedOffer={selectedOffer}
            />
        </Container>
    );
};

export default ProductOffers;
